import React, { useMemo, useState } from 'react';
import { alertService, type Alert } from '../../services/alertService';
import { CreateAlertForm } from './CreateAlertForm';
import { AlertsList } from './AlertsList';
import { TelegramPreview } from './TelegramPreview';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAlerts } from '../../context/AlertContext';
import { AlertsStats } from './AlertsStats';
type AlertStatus = 'all'| 'active' | 'triggered' | 'cancelled';

const STATUS_CONFIG: Record<AlertStatus, { label: string; activeClass: string; activeBadge: string; dot: string | null }> = {
  all:       { label: 'All',       activeClass: 'text-violet-700 dark:text-[#a78bfa]', activeBadge: 'bg-[#a78bfa]/15 text-[#a78bfa]',           dot: null },
  active:    { label: 'Active',    activeClass: 'text-emerald-700 dark:text-[#10d98e]', activeBadge: 'bg-[#10d98e]/15 text-[#10d98e]',           dot: 'bg-[#10d98e]' },
  triggered: { label: 'Triggered', activeClass: 'text-amber-700 dark:text-amber-400',  activeBadge: 'bg-amber-400/15 text-amber-400',            dot: 'bg-amber-400' },
  cancelled: { label: 'Cancelled', activeClass: 'text-gray-600 dark:text-[#c2d0e0]',   activeBadge: 'bg-gray-200 dark:bg-[#1d2d47] text-[#c2d0e0]', dot: null },
};

export default function AlertsPage() {
  const { alerts, isLoading, refreshAlerts } = useAlerts();
  const [activeFilter, setActiveFilter] = useState<AlertStatus>('all');
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [recreatingId, setRecreatingId] = useState<string | null>(null);

  const loading = isLoading;

  const handleDelete = async (id: string) => {
    try {
      await alertService.deleteAlert(id);
      await refreshAlerts();//עדכון הנתונים מול השרת
      toast.success('Alert cancelled');
    } catch (err) {
      console.error('Error canceling alert:', err);
      await refreshAlerts();
      toast.error('Unable to cancel alert');
    }
  };
  const handleRecreate = async (alertToRecreate: Alert) => {
    if (recreatingId) return;

    setRecreatingId(alertToRecreate.id);
    const toastId = toast.loading(`Recreating alert for ${alertToRecreate.ticker}…`);

    try {
      await alertService.createAlert({
        ticker: alertToRecreate.ticker,
        alert_type: alertToRecreate.alert_type,
        target_value: alertToRecreate.target_value,
        channels: alertToRecreate.channels,
      });
      await refreshAlerts();
      toast.success(`Alert recreated for ${alertToRecreate.ticker}`, { id: toastId });
    } catch (err: any) {
      const status = err.response?.status || err.status;
      const backendMessage = err.response?.data?.message;

      if (status === 400) {
        toast.error(backendMessage || 'Ticker does not exist in the stock market.', { id: toastId });
      } else if (status === 409) {
        toast.error('This alert already exists.', { id: toastId });
      } else if (status === 503) {
        toast.error('Market data is temporarily unavailable. Please try again in a moment.', { id: toastId });
      } else {
        toast.error('Something went wrong. Please try again.', { id: toastId });
      }
    } finally {
      setRecreatingId(null);
    }
  };

  const filteredAlerts = useMemo(() => {
    return activeFilter === 'all' ? alerts : alerts.filter((a) => a.status === activeFilter);
  }, [activeFilter, alerts]);

  const triggeredAlerts = useMemo(() => {
    return alerts.filter((alert) => alert.status === 'triggered' || alert.status === 'cancelled');
  }, [alerts]);

  const counts = useMemo(() => ({
    all: alerts.length,
    active: alerts.filter((a) => a.status === 'active').length,
    triggered: triggeredAlerts.length,
    cancelled: alerts.filter((a) => a.status === 'cancelled').length,
  }), [alerts, triggeredAlerts]);

  return (
    <>
      <div className="space-y-6">
        {/* ── Hero Banner ── */}
        <div className="relative rounded-2xl overflow-hidden border border-violet-100 dark:border-[#1d2d47] bg-gradient-to-br from-violet-50 via-indigo-50/60 to-white dark:from-[#0e1624] dark:via-[#0f0d20]/50 dark:to-[#080c14] p-6">
          <div className="pointer-events-none absolute -top-8 -right-8 w-48 h-48 rounded-full bg-gradient-to-br from-[#a78bfa] to-[#6366f1] opacity-[0.08] dark:opacity-[0.12] blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/3 w-32 h-32 rounded-full bg-[#4f8ff8] opacity-[0.05] dark:opacity-[0.08] blur-2xl" />

          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-[22px] font-bold tracking-tight bg-gradient-to-r from-[#a78bfa] via-[#818cf8] to-[#6366f1] bg-clip-text text-transparent">
                Alerts
              </h1>
              <p className="text-[13px] text-gray-500 dark:text-[#a3b6cc] mt-0.5">
                Price & condition alerts
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-[#0e1624] border border-violet-200 dark:border-[#a78bfa]/30 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500 dark:bg-[#a78bfa]" />
                </span>
                <span className="text-[11px] font-semibold text-violet-700 dark:text-[#a78bfa] font-mono tracking-wide">
                  {counts.active} ACTIVE
                </span>
              </div>
              <button
                onClick={() => setIsFormOpen(true)}
                className="lg:hidden flex items-center gap-1.5 bg-[#a78bfa] text-white px-4 py-2 rounded-xl text-xs font-bold"
              >
                <Plus size={14} strokeWidth={3} /> Create
              </button>
            </div>
          </div>

          {!loading && (
            <div className="mt-4">
              <AlertsStats alerts={alerts} />
            </div>
          )}
        </div>
      <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-[#080c14]/60 border border-gray-200 dark:border-[#1d2d47] rounded-xl p-1 w-fit overflow-x-auto no-scrollbar">
        {(['all', 'active', 'triggered', 'cancelled'] as AlertStatus[]).map((tab) => {
          const isActive = activeFilter === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveFilter(tab)}
              className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-all duration-150 whitespace-nowrap flex items-center gap-1.5 ${
                isActive
                  ? `bg-white dark:bg-[#141e30] shadow-sm ${STATUS_CONFIG[tab].activeClass}`
                  : 'text-gray-500 dark:text-[#c2d0e0] hover:text-gray-800 dark:hover:text-[#d5deea]'
              }`}
            >
              {STATUS_CONFIG[tab].dot && (
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[tab].dot} shrink-0`} />
              )}
              {STATUS_CONFIG[tab].label}
              <span className={`px-1.5 py-0 rounded text-[9px] font-mono ${
                isActive
                  ? STATUS_CONFIG[tab].activeBadge
                  : 'bg-gray-200/80 dark:bg-[#141822] text-gray-500 dark:text-[#c2d0e0]'
              }`}>
                {counts[tab]}
              </span>
            </button>
          );
        })}
      </div>
    
      {/* ── Main Layout Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_330px] gap-6 items-start">
        
        {/* Left: Alerts List */}
        <div className="w-full min-w-0">
          <AlertsList
            alerts={filteredAlerts}
            loading={loading}
            onDelete={handleDelete}
            onRecreate={handleRecreate}
            recreatingId={recreatingId}
          />
        </div>

        {/* Right Sidebar — scrolls independently inside the viewport */}
        <aside className="hidden lg:block w-full sticky top-[96px] max-h-[calc(100vh-112px)] overflow-y-auto overscroll-contain space-y-6 pr-0.5 [scrollbar-width:thin]">
          <CreateAlertForm onSuccess={refreshAlerts} />

          <div className="rounded-2xl border border-gray-200 dark:border-cat-border bg-white dark:bg-cat-surface p-4 shadow-sm">
            {triggeredAlerts.length === 0 ? (
              <div className="rounded-xl border border-cat-accent/10 bg-[#4f8ff8]/5 p-6 text-center">
                <p className="text-[13px] text-gray-500 dark:text-cat-text-muted italic">
                  Select a triggered alert to view its live Telegram preview.
                </p>
              </div>
            ) : (
              <TelegramPreview alerts={triggeredAlerts} />
            )}
          </div>
        </aside>
      </div> 

      {/* Mobile Modal Form */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:hidden">
          <div className="fixed inset-0 bg-black/80" onClick={() => setIsFormOpen(false)} />
          <div className="relative z-10 w-full max-w-md">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-3 right-3 z-10 text-gray-500 dark:text-cat-text-muted hover:text-gray-800 dark:hover:text-cat-text-main"
            >
              <X size={16} />
            </button>
            <CreateAlertForm onSuccess={async () => { await refreshAlerts(); setIsFormOpen(false); }} />
          </div>
        </div>
      )}
    </div>
    </>

  );
}